import React, { useState } from 'react';
import './Calendar.css';
import { ChevronLeft, ChevronRight, Plus, X, Search, Filter, AlertCircle, CheckCircle2, Clock, Building2, UserRound, Layers3 } from 'lucide-react';

const Calendar = ({ appointments = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('month'); // month, week, agenda
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClinicId, setSelectedClinicId] = useState('all');
  const [selectedProfileId, setSelectedProfileId] = useState('all');

  const clinicOptions = [
    { id: 'all', name: 'All Clinics' },
    { id: 'downtown', name: 'RuaMedical Downtown' },
    { id: 'north', name: 'RuaMedical North' },
    { id: 'telehealth', name: 'RuaMedical Telehealth Hub' }
  ];

  const profileOptions = [
    { id: 'all', name: 'All Profiles' },
    { id: 'provider-admin', name: 'Provider Admin' },
    { id: 'scheduler', name: 'Scheduler Team' },
    { id: 'billing', name: 'Billing Team' },
    { id: 'nurse', name: 'Nursing Team' }
  ];

  const [formData, setFormData] = useState({
    patientName: '',
    service: '',
    dateTime: '',
    status: 'Confirmed',
    type: 'Consultation',
    color: '#3B82F6'
  });

  const [rescheduleData, setRescheduleData] = useState({
    newDate: '',
    newTime: '',
    reason: '',
    selectedRoom: ''
  });

  const [cancelData, setCancelData] = useState({
    reason: '',
    notifyPatient: true
  });

  // Use appointments passed from parent, or use default example events if none provided
  const defaultEvents = [
    {
      id: 1,
      patientName: 'John Smith',
      service: 'Annual Checkup',
      dateTime: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 1, 10, 0),
      status: 'Confirmed',
      type: 'Consultation',
      color: '#3B82F6',
      clinicId: 'downtown',
      profileId: 'provider-admin'
    },
    {
      id: 2,
      patientName: 'Emma Wilson',
      service: 'Follow-up',
      dateTime: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 2, 14, 30),
      status: 'Confirmed',
      type: 'Follow-up',
      color: '#10B981',
      clinicId: 'north',
      profileId: 'nurse'
    },
    {
      id: 3,
      patientName: 'Michael Brown',
      service: 'Lab Work',
      dateTime: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 3, 9, 0),
      status: 'Pending',
      type: 'Lab',
      color: '#F59E0B',
      clinicId: 'downtown',
      profileId: 'nurse'
    },
    {
      id: 4,
      patientName: 'Sarah Davis',
      service: 'Dental Cleaning',
      dateTime: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 5, 11, 0),
      status: 'Confirmed',
      type: 'Dental',
      color: '#8B5CF6',
      clinicId: 'north',
      profileId: 'scheduler'
    },
    {
      id: 5,
      patientName: 'Robert Taylor',
      service: 'Physical Therapy',
      dateTime: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 6, 15, 0),
      status: 'Confirmed',
      type: 'Therapy',
      color: '#EC4899',
      clinicId: 'telehealth',
      profileId: 'provider-admin'
    },
    {
      id: 6,
      patientName: 'Lisa Anderson',
      service: 'Eye Exam',
      dateTime: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 8, 13, 0),
      status: 'Confirmed',
      type: 'Vision',
      color: '#06B6D4',
      clinicId: 'telehealth',
      profileId: 'billing'
    }
  ];

  const sourceAppointments = appointments && appointments.length > 0 ? appointments : defaultEvents;

  const allAppointments = sourceAppointments.map((appointment, index) => ({
    id: appointment.id || index + 1,
    ...appointment,
    color: appointment.color || '#3B82F6',
    clinicId: appointment.clinicId || 'downtown',
    profileId: appointment.profileId || 'provider-admin'
  }));

  const scopedAppointments = allAppointments.filter((appointment) => {
    const clinicMatch = selectedClinicId === 'all' || appointment.clinicId === selectedClinicId;
    const profileMatch = selectedProfileId === 'all' || appointment.profileId === selectedProfileId;
    return clinicMatch && profileMatch;
  });

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month];
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push({ day: '', isEmpty: true });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isToday = date.toDateString() === new Date().toDateString();
      const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
      
      const dayAppointments = scopedAppointments.filter(apt => {
        const aptDate = new Date(apt.dateTime);
        return aptDate.toDateString() === date.toDateString();
      });

      days.push({
        day,
        date,
        isToday,
        isSelected,
        hasAppointments: dayAppointments.length > 0,
        appointments: dayAppointments
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  const getSelectedDateAppointments = () => {
    if (!selectedDate) return [];
    return scopedAppointments.filter(apt => {
      const aptDate = new Date(apt.dateTime);
      return aptDate.toDateString() === selectedDate.toDateString();
    });
  };

  const getFilteredAppointments = () => {
    let filtered = scopedAppointments;
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(apt => apt.status.toLowerCase().includes(filterStatus.toLowerCase()));
    }
    
    if (searchTerm) {
      filtered = filtered.filter(apt => 
        apt.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.service.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered.sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
  };

  const selectedDateAppointments = getSelectedDateAppointments();
  const filteredAppointments = getFilteredAppointments();

  const handleCreateEvent = () => {
    if (formData.patientName && formData.service && formData.dateTime) {
      console.log('Created event:', formData);
      setFormData({
        patientName: '',
        service: '',
        dateTime: '',
        status: 'Confirmed',
        type: 'Consultation',
        color: '#3B82F6'
      });
      setShowCreateModal(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleRescheduleClick = () => {
    setShowEventModal(false);
    setShowRescheduleModal(true);
    setRescheduleData({
      newDate: '',
      newTime: '',
      reason: '',
      selectedRoom: ''
    });
  };

  const handleCancelClick = () => {
    setShowEventModal(false);
    setShowCancelModal(true);
  };

  const handleResourceSelection = () => {
    setShowRescheduleModal(false);
    setShowResourceModal(true);
  };

  const handleConfirmReschedule = () => {
    console.log('Rescheduled appointment:', {
      appointment: selectedEvent,
      ...rescheduleData
    });
    setShowResourceModal(false);
    setShowRescheduleModal(false);
    setSelectedEvent(null);
    alert('Appointment rescheduled successfully!');
  };

  const handleConfirmCancel = () => {
    console.log('Cancelled appointment:', {
      appointment: selectedEvent,
      ...cancelData
    });
    setShowCancelModal(false);
    setSelectedEvent(null);
    alert('Appointment cancelled successfully!');
  };

  return (
    <div className="google-calendar">
      {/* Header */}
      <div className="gc-header">
        <div className="gc-header-left">
          <h1 className="gc-title">Calendar</h1>
          <button className="gc-btn-today" onClick={goToToday}>Today</button>
        </div>
        <div className="gc-header-center">
          <button className="gc-btn-nav" onClick={goToPreviousMonth}>
            <ChevronLeft size={20} />
          </button>
          <h2 className="gc-month-year">
            {getMonthName(currentMonth)} {currentYear}
          </h2>
          <button className="gc-btn-nav" onClick={goToNextMonth}>
            <ChevronRight size={20} />
          </button>
        </div>
        <div className="gc-header-right">
          <div className="gc-view-modes">
            {['month', 'week', 'agenda'].map(mode => (
              <button
                key={mode}
                className={`gc-view-btn ${viewMode === mode ? 'active' : ''}`}
                onClick={() => setViewMode(mode)}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          <button className="gc-btn-create" onClick={() => setShowCreateModal(true)}>
            <Plus size={20} />
            Create
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="gc-organization-strip">
        <label className="gc-organization-field">
          <span><Building2 size={14} /> Clinic</span>
          <select value={selectedClinicId} onChange={(e) => setSelectedClinicId(e.target.value)}>
            {clinicOptions.map((clinic) => (
              <option key={clinic.id} value={clinic.id}>{clinic.name}</option>
            ))}
          </select>
        </label>

        <label className="gc-organization-field">
          <span><UserRound size={14} /> Profile Group</span>
          <select value={selectedProfileId} onChange={(e) => setSelectedProfileId(e.target.value)}>
            {profileOptions.map((profile) => (
              <option key={profile.id} value={profile.id}>{profile.name}</option>
            ))}
          </select>
        </label>

        <div className="gc-scale-badges">
          <span><Layers3 size={13} /> 3 Clinics</span>
          <span><UserRound size={13} /> 124 Profiles</span>
          <span>100+ Supported</span>
        </div>
      </div>

      <div className="gc-controls">
        <div className="gc-search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="gc-filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Events</option>
          <option value="confirmed">Confirmed</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {viewMode === 'month' && (
        <div className="gc-calendar-wrapper">
          {/* Weekday Header */}
          <div className="gc-weekday-header">
            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, idx) => (
              <div key={day} className="gc-weekday-cell">
                <span className="gc-day-name">{day}</span>
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="gc-calendar-grid">
            {calendarDays.map((dayData, index) => {
              const isOtherMonth = dayData.isEmpty;
              return (
                <div
                  key={index}
                  className={`gc-day-cell ${isOtherMonth ? 'other-month' : ''} ${dayData.isToday ? 'today' : ''} ${dayData.isSelected ? 'selected' : ''}`}
                  onClick={() => !isOtherMonth && setSelectedDate(dayData.date)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="gc-day-number">
                    {!isOtherMonth && dayData.day}
                  </div>
                  {!isOtherMonth && dayData.hasAppointments && (
                    <div className="gc-day-events">
                      {dayData.appointments.slice(0, 2).map((event, eventIdx) => (
                        <div
                          key={eventIdx}
                          className="gc-event-item"
                          style={{ backgroundColor: event.color }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event);
                          }}
                          title={`${event.service} - ${event.patientName}`}
                        >
                          <span className="gc-event-time">
                            {new Date(event.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                          </span>
                          <span className="gc-event-name">{event.service}</span>
                        </div>
                      ))}
                      {dayData.appointments.length > 2 && (
                        <div className="gc-more-events">
                          +{dayData.appointments.length - 2} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewMode === 'agenda' && (
        <div className="gc-agenda-view">
          <div className="gc-agenda-list">
            {filteredAppointments.length > 0 ? (
              filteredAppointments.map((event) => (
                <div 
                  key={event.id} 
                  className="gc-agenda-item"
                  onClick={() => handleEventClick(event)}
                >
                  <div className="gc-agenda-date">
                    {new Date(event.dateTime).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      weekday: 'short'
                    })}
                  </div>
                  <div className="gc-agenda-time">
                    {new Date(event.dateTime).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true
                    })}
                  </div>
                  <div className="gc-agenda-content">
                    <h4>{event.service}</h4>
                    <p>{event.patientName}</p>
                  </div>
                  <div className="gc-agenda-badges">
                    <span className="gc-badge" style={{ backgroundColor: event.color }}>
                      {event.type}
                    </span>
                    <span className={`gc-badge gc-status-${event.status.toLowerCase().replace(' ', '-')}`}>
                      {event.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="gc-no-events">
                <p>No events found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sidebar */}
      {selectedDate && (
        <div className="gc-sidebar">
          <div className="gc-sidebar-header">
            <h3>
              {selectedDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h3>
            <button 
              className="gc-sidebar-close"
              onClick={() => setSelectedDate(null)}
            >
              ✕
            </button>
          </div>

          <button className="gc-sidebar-create-event" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} />
            Create event
          </button>

          <div className="gc-sidebar-events">
            {selectedDateAppointments.length > 0 ? (
              <div className="gc-events-list">
                {selectedDateAppointments.map((event, idx) => (
                  <div 
                    key={idx} 
                    className="gc-sidebar-event" 
                    style={{ borderLeftColor: event.color }}
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="gc-sidebar-event-time">
                      {new Date(event.dateTime).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      })}
                    </div>
                    <div className="gc-sidebar-event-details">
                      <h4 className="gc-sidebar-event-title">{event.service}</h4>
                      <p className="gc-sidebar-event-doctor">{event.patientName}</p>
                      <div className="gc-sidebar-event-meta">
                        <span className="gc-event-type">{event.type}</span>
                        <span className={`gc-event-status ${event.status.toLowerCase().replace(' ', '-')}`}>
                          {event.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="gc-no-events">
                <p>No events scheduled</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showCreateModal && (
        <div className="gc-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gc-modal-header">
              <h2>Create Event</h2>
              <button className="gc-modal-close" onClick={() => setShowCreateModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="gc-modal-body">
              <div className="gc-form-group">
                <label>Patient Name</label>
                <input 
                  type="text" 
                  name="patientName" 
                  value={formData.patientName}
                  onChange={handleInputChange}
                  placeholder="Enter patient name"
                />
              </div>
              <div className="gc-form-group">
                <label>Service</label>
                <input 
                  type="text" 
                  name="service" 
                  value={formData.service}
                  onChange={handleInputChange}
                  placeholder="Enter service type"
                />
              </div>
              <div className="gc-form-row">
                <div className="gc-form-group">
                  <label>Type</label>
                  <select name="type" value={formData.type} onChange={handleInputChange}>
                    <option>Consultation</option>
                    <option>Follow-up</option>
                    <option>Lab</option>
                    <option>Dental</option>
                    <option>Therapy</option>
                    <option>Vision</option>
                  </select>
                </div>
                <div className="gc-form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleInputChange}>
                    <option>Confirmed</option>
                    <option>Pending</option>
                    <option>Cancelled</option>
                  </select>
                </div>
              </div>
              <div className="gc-form-group">
                <label>Date & Time</label>
                <input 
                  type="datetime-local" 
                  name="dateTime" 
                  value={formData.dateTime}
                  onChange={handleInputChange}
                />
              </div>
              <div className="gc-form-group">
                <label>Color</label>
                <div className="gc-color-picker">
                  {['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'].map(color => (
                    <button
                      key={color}
                      className={`gc-color-option ${formData.color === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="gc-modal-footer">
              <button className="gc-btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="gc-btn-primary" onClick={handleCreateEvent}>Create Event</button>
            </div>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {showEventModal && selectedEvent && (
        <div className="gc-modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gc-modal-header">
              <h2>Event Details</h2>
              <button className="gc-modal-close" onClick={() => setShowEventModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="gc-modal-body">
              <div className="gc-event-details-content">
                <div className="gc-detail-item">
                  <span className="gc-detail-label">Service:</span>
                  <span className="gc-detail-value">{selectedEvent.service}</span>
                </div>
                <div className="gc-detail-item">
                  <span className="gc-detail-label">Patient:</span>
                  <span className="gc-detail-value">{selectedEvent.patientName}</span>
                </div>
                <div className="gc-detail-item">
                  <span className="gc-detail-label">Date & Time:</span>
                  <span className="gc-detail-value">
                    {new Date(selectedEvent.dateTime).toLocaleString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="gc-detail-item">
                  <span className="gc-detail-label">Type:</span>
                  <span className="gc-detail-value">{selectedEvent.type}</span>
                </div>
                <div className="gc-detail-item">
                  <span className="gc-detail-label">Status:</span>
                  <span className={`gc-detail-value gc-status-${selectedEvent.status.toLowerCase().replace(' ', '-')}`}>
                    {selectedEvent.status}
                  </span>
                </div>
              </div>
            </div>
            <div className="gc-modal-footer">
              <button className="gc-btn-secondary" onClick={() => setShowEventModal(false)}>Close</button>
              <button className="gc-btn-warning" onClick={handleRescheduleClick}>Reschedule</button>
              <button className="gc-btn-danger" onClick={handleCancelClick}>Cancel Appointment</button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedEvent && (
        <div className="gc-modal-overlay" onClick={() => setShowRescheduleModal(false)}>
          <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gc-modal-header">
              <h2>Reschedule Appointment</h2>
              <button className="gc-modal-close" onClick={() => setShowRescheduleModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="gc-modal-body">
              <div className="gc-appt-summary">
                <p><strong>Current Appointment:</strong></p>
                <p>{selectedEvent.service} with {selectedEvent.patientName}</p>
                <p>{new Date(selectedEvent.dateTime).toLocaleString()}</p>
              </div>
              <div className="gc-form-group">
                <label>Reason for Rescheduling</label>
                <input 
                  type="text"
                  placeholder="e.g., Schedule conflict, Patient request"
                  value={rescheduleData.reason}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, reason: e.target.value }))}
                />
              </div>
              <div className="gc-form-row">
                <div className="gc-form-group">
                  <label>New Date</label>
                  <input 
                    type="date"
                    value={rescheduleData.newDate}
                    onChange={(e) => setRescheduleData(prev => ({ ...prev, newDate: e.target.value }))}
                  />
                </div>
                <div className="gc-form-group">
                  <label>New Time</label>
                  <input 
                    type="time"
                    value={rescheduleData.newTime}
                    onChange={(e) => setRescheduleData(prev => ({ ...prev, newTime: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="gc-modal-footer">
              <button className="gc-btn-secondary" onClick={() => setShowRescheduleModal(false)}>Cancel</button>
              <button className="gc-btn-primary" onClick={handleResourceSelection}>Continue to Room Selection</button>
            </div>
          </div>
        </div>
      )}

      {/* Resource/Room Selection Modal */}
      {showResourceModal && selectedEvent && (
        <div className="gc-modal-overlay" onClick={() => setShowResourceModal(false)}>
          <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gc-modal-header">
              <h2>Select Room & Resources</h2>
              <button className="gc-modal-close" onClick={() => setShowResourceModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="gc-modal-body">
              <div className="gc-form-group">
                <label>Available Rooms</label>
                <div className="gc-room-selector">
                  {['Room 101', 'Room 202', 'Room 305', 'Telehealth', 'Lab Suite A'].map((room, idx) => (
                    <button
                      key={idx}
                      className={`gc-room-option ${rescheduleData.selectedRoom === room ? 'selected' : ''}`}
                      onClick={() => setRescheduleData(prev => ({ ...prev, selectedRoom: room }))}
                    >
                      <CheckCircle2 size={18} />
                      <span>{room}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="gc-resource-info">
                <h3>Appointment Summary</h3>
                <div className="gc-detail-item">
                  <span className="gc-detail-label">Service:</span>
                  <span className="gc-detail-value">{selectedEvent.service}</span>
                </div>
                <div className="gc-detail-item">
                  <span className="gc-detail-label">New Date & Time:</span>
                  <span className="gc-detail-value">{rescheduleData.newDate} at {rescheduleData.newTime}</span>
                </div>
                <div className="gc-detail-item">
                  <span className="gc-detail-label">Room:</span>
                  <span className="gc-detail-value">{rescheduleData.selectedRoom || 'Not selected'}</span>
                </div>
              </div>
            </div>
            <div className="gc-modal-footer">
              <button className="gc-btn-secondary" onClick={() => setShowResourceModal(false)}>Back</button>
              <button className="gc-btn-primary" onClick={handleConfirmReschedule} disabled={!rescheduleData.newDate || !rescheduleData.newTime || !rescheduleData.selectedRoom}>Confirm Reschedule</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedEvent && (
        <div className="gc-modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="gc-modal gc-modal-warning" onClick={(e) => e.stopPropagation()}>
            <div className="gc-modal-header">
              <h2>Cancel Appointment?</h2>
              <button className="gc-modal-close" onClick={() => setShowCancelModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="gc-modal-body">
              <div className="gc-warning-content">
                <AlertCircle size={32} color="#EF4444" />
                <p><strong>Are you sure you want to cancel this appointment?</strong></p>
                <div className="gc-appt-summary">
                  <p>{selectedEvent.service} with {selectedEvent.patientName}</p>
                  <p>{new Date(selectedEvent.dateTime).toLocaleString()}</p>
                </div>
              </div>
              <div className="gc-form-group">
                <label>Cancellation Reason</label>
                <textarea 
                  placeholder="Please provide a reason for cancellation..."
                  value={cancelData.reason}
                  onChange={(e) => setCancelData(prev => ({ ...prev, reason: e.target.value }))}
                  rows="4"
                />
              </div>
              <div className="gc-form-group">
                <label className="gc-checkbox-label">
                  <input 
                    type="checkbox"
                    checked={cancelData.notifyPatient}
                    onChange={(e) => setCancelData(prev => ({ ...prev, notifyPatient: e.target.checked }))}
                  />
                  <span>Notify patient immediately</span>
                </label>
              </div>
            </div>
            <div className="gc-modal-footer">
              <button className="gc-btn-secondary" onClick={() => setShowCancelModal(false)}>Keep Appointment</button>
              <button className="gc-btn-danger" onClick={handleConfirmCancel}>Confirm Cancellation</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
